import React from 'react';
import { Navigate } from 'react-router-dom';

// Hooks
import { useFeatureFlagWithLoading } from '../hooks/useFeatureFlag';

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface FeatureRouteProps {
  /**
   * Nombre del feature flag a verificar
   * Ejemplo: 'feature-surveillance-route'
   */
  feature: string;
  /**
   * Componentes hijos a renderizar si el feature está habilitado
   */
  children: React.ReactNode;
  /**
   * Ruta a la que redirigir si el feature no está habilitado
   * Default: '/work-orders'
   */
  fallbackPath?: string;
  /**
   * Si es true, muestra los hijos aunque el feature esté deshabilitado
   * Útil para desarrollo/testing
   */
  bypassInDev?: boolean;
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

/**
 * Componente wrapper que protege rutas basándose en feature flags.
 *
 * Si el feature no está habilitado para el tenant actual,
 * redirige al usuario a la ruta de fallback.
 *
 * @example
 * <Route
 *   path="surveillance-route"
 *   element={
 *     <FeatureRoute feature="feature-surveillance-route">
 *       <SurveillanceRouteLayout />
 *     </FeatureRoute>
 *   }
 * />
 */
export function FeatureRoute({
  feature,
  children,
  fallbackPath = '/work-orders',
  bypassInDev = false,
}: FeatureRouteProps): JSX.Element | null {
  const { isEnabled, loading } = useFeatureFlagWithLoading(feature, false);

  // En desarrollo, opcionalmente bypass
  if (bypassInDev && import.meta.env.DEV) {
    console.log(
      `[FeatureRoute] DEV bypass for feature "${feature}" - showing content`
    );
    return <>{children}</>;
  }

  // El flag AÚN está cargando: NO decidir todavía. Si redirigiéramos aquí con el
  // default (false), el `<Navigate>` desmontaría este componente y Apollo
  // ABORTARÍA la query `getConfig` en vuelo ANTES de recibir el valor real
  // ("true") → el flag no se resolvía nunca y la ruta quedaba inaccesible en
  // build de producción aunque el backend devolviese "true" (bug RV beta
  // 2026-07-22: getConfig aborted → respuesta vacía → redirect infinito).
  // Renderizamos `null` (el componente sigue montado → la query se completa).
  if (loading) {
    return null;
  }

  if (!isEnabled) {
    console.warn(
      `[FeatureRoute] Feature "${feature}" is not enabled - redirecting to ${fallbackPath}`
    );
    return <Navigate to={fallbackPath} replace />;
  }

  return <>{children}</>;
}

export default FeatureRoute;

